/**
 * Checkout Page - Dedicated page for mobile checkout
 * Access at: /checkout-channel-invite
 * 
 * Full-screen Stripe checkout experience optimized for mobile
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_APPEARANCE, CREATE_PAYMENT_INTENT_ENDPOINT, getPackPricing as getPackPricingStatic } from "@/config/stripe";
import { usePackConfig } from "@/lib/usePackConfig";
import { Loader2 } from "lucide-react";
import {
  computeStripePackCheckoutAmounts,
  getOrCreateSessionWatcherLuckPercent,
  getWatcherLuckDiscountMaxPercentClient,
} from "@/lib/watcherLuckDiscount";
import { trackStripePayFailurePostHog } from "@/lib/posthogAnalytics";
import { getDataFastStripeMetadata, trackDataFastGoal } from "@/lib/datafast";

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get parameters from URL
  const packId = searchParams.get("pack") || "channel_invite";
  const countryCode = searchParams.get("country") || "US";
  const language = (searchParams.get("lang") || "es") as "en" | "es";
  
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const checkoutStripeUnavailableTrackedRef = useRef(false);

  const { getPackPricing } = usePackConfig();
  // Para sorpresa/exclusive asegurar precio desde static y así siempre generar el custom element
  const packPricing = (["sorpresa", "exclusive"].includes(packId) ? getPackPricingStatic(packId, countryCode) : null) ?? getPackPricing(packId, countryCode);

  const checkoutWatcherLuckEligible =
    getWatcherLuckDiscountMaxPercentClient() > 0 &&
    !(countryCode.toUpperCase() === "AR" && packId === "channel_invite");

  const checkoutWatcherLuckPercent = checkoutWatcherLuckEligible ? getOrCreateSessionWatcherLuckPercent() : 0;

  const checkoutAmounts =
    packPricing != null
      ? computeStripePackCheckoutAmounts({
          baseAmountCents: packPricing.amount,
          packId,
          watcherLuckPercent: checkoutWatcherLuckPercent,
        })
      : null;

  const createPaymentIntent = async () => {
    if (!packPricing) return;
    // AR channel invite también es Stripe (MercadoPago retirado).
    setError(null);
    setIsLoading(true);
    let trackedFailure = false;
    try {
      const apiUrl = typeof window !== "undefined" ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}` : CREATE_PAYMENT_INTENT_ENDPOINT;
      // Custom Stripe: always USD regardless of country; amount is in USD cents
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: packPricing.amount,
          currency: "usd",
          packId: packId,
          source: window.location.pathname,
          country: countryCode || undefined,
          watcherLuckDiscountPercent: checkoutWatcherLuckEligible ? checkoutWatcherLuckPercent : 0,
          ...getDataFastStripeMetadata(),
          metadata: {
            ...getDataFastStripeMetadata(),
          },
        }),
      });
      trackDataFastGoal("initiate_checkout", {
        flow: "checkout_channel_invite",
        pack_id: packId,
        gateway: "stripe",
      });
      const text = await response.text();
      if (!response.ok) {
        let errMsg = "Failed to create payment intent";
        let stripeCode: string | undefined;
        try {
          const errorData = JSON.parse(text) as {
            error?: string;
            message?: string;
            code?: string;
            redirect_url?: string;
          };

          errMsg = errorData.message || errorData.error || errMsg;
          stripeCode = errorData.code;
        } catch {
          if (response.status === 404) errMsg = language === "es" ? "No se encontró el servicio de pago." : "Payment service not found.";
          else if (response.status >= 500) errMsg = language === "es" ? "Error del servidor. Intenta de nuevo." : "Server error. Please try again.";
        }
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          http_status: response.status,
          stripe_error_code: stripeCode,
          error_message: errMsg,
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(errMsg);
      }
      let data: { clientSecret?: string };
      try {
        data = JSON.parse(text) as { clientSecret?: string };
      } catch {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: "invalid_json_response",
          http_status: response.status,
          error_message: "Invalid JSON from payment API",
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(
          language === "es" ? "Respuesta inválida del servidor." : "Invalid server response.",
        );
      }
      if (!data?.clientSecret) {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: "missing_client_secret",
          error_message: "Missing clientSecret in API response",
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(
          language === "es" ? "No se pudo inicializar el checkout." : "Could not initialize checkout.",
        );
      }
      setClientSecret(data.clientSecret);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to initialize checkout";
      if (!trackedFailure) {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: err instanceof TypeError ? "network_error" : "unknown",
          error_message: message,
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) {
      trackStripePayFailurePostHog({
        stage: "invalid_config",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "missing_publishable_key",
        error_message: "Stripe not configured",
        pack_id: packId,
      });
      setError("Stripe not configured");
      setIsLoading(false);
      return;
    }
    if (!packPricing) {
      trackStripePayFailurePostHog({
        stage: "invalid_config",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "invalid_pack_pricing",
        error_message: language === "es" ? "Producto no válido" : "Invalid product",
        pack_id: packId,
      });
      setError(language === "es" ? "Producto no válido" : "Invalid product");
      setIsLoading(false);
      return;
    }
    createPaymentIntent();
  }, [packId, countryCode]);
  
  const handleSuccess = (paymentIntentId?: string, paymentIntentSecret?: string) => {
    // Determine thank you page based on language
    const thankYouPath = language === "es" ? "/gracias" : "/thank-you";
    
    // Build redirect URL with payment_intent parameters
    const params = new URLSearchParams();
    if (paymentIntentId) {
      params.append("payment_intent", paymentIntentId);
    }
    if (paymentIntentSecret) {
      params.append("payment_intent_client_secret", paymentIntentSecret);
    }
    
    const redirectUrl = `${thankYouPath}?${params.toString()}`;
    window.location.href = redirectUrl;
  };
  
  const handleError = (error: string) => {
    setError(error);
  };
  
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  // Loading state
  if (isLoading) {
    const loadingText = language === "es" ? "Preparando checkout..." : "Preparing checkout...";
    return (
      <div
        className="min-h-screen bg-black flex items-center justify-center p-4 font-body"
        aria-busy="true"
        aria-live="polite"
        aria-label={loadingText}
      >
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" aria-hidden />
          <p className="text-white/60 text-sm font-medium">{loadingText}</p>
        </div>
      </div>
    );
  }

  // Error state (with retry for better UX)
  if (error || !packPricing || !clientSecret) {
    const errorTitle = language === "es" ? "Error en el pago" : "Payment error";
    const errorMsg = error || (language === "es" ? "No se pudo inicializar el checkout" : "Could not initialize checkout");
    const retryLabel = language === "es" ? "Reintentar" : "Try again";
    const backLabel = language === "es" ? "Volver" : "Go back";
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 font-body" role="alert">
        <div className="max-w-md w-full bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center space-y-4">
          <div className="text-red-400 text-4xl mb-2" aria-hidden>⚠️</div>
          <h2 className="text-xl font-bold text-white font-display">{errorTitle}</h2>
          <p className="text-white/60 text-sm font-medium">{errorMsg}</p>
          <div className="flex flex-col gap-3 pt-2">
            {packPricing && (
              <button
                type="button"
                onClick={createPaymentIntent}
                className="w-full bg-white text-black font-bold py-3 px-6 rounded-xl hover:bg-white/90 active:bg-white/95 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              >
                {retryLabel}
              </button>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="w-full bg-white/10 text-white font-bold py-3 px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Checkout form
  const stripePromise = getStripe();
  if (!stripePromise) {
    if (!checkoutStripeUnavailableTrackedRef.current) {
      checkoutStripeUnavailableTrackedRef.current = true;
      trackStripePayFailurePostHog({
        stage: "stripe_js_unavailable",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "stripe_promise_null",
        pack_id: packId,
      });
    }
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/60">Stripe not configured</p>
      </div>
    );
  }
  
  return (
    // h-dvh (dynamic viewport height) handles iOS in-app browsers correctly:
    // unlike min-h-screen/100vh which ignores the address bar, dvh adjusts
    // to the actual visible area so the Pay button is never below the fold.
    <div 
      className="h-dvh flex flex-col bg-black font-body"
      style={{
        // Safari iOS: Prevent bounce scrolling on main container
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        // Android: Ensure proper rendering
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Elements 
        stripe={stripePromise} 
        options={{ 
          clientSecret,
          appearance: STRIPE_APPEARANCE,
          locale: language === "es" ? "es" : "en",
        }}
      >
        {/* Elements is a context provider with no DOM node — this div
            gives StripeCheckoutForm an explicit flex-1 height to inherit */}
        <div className="flex-1 flex flex-col min-h-0">
          <StripeCheckoutForm
            amount={checkoutAmounts?.finalAmountCents ?? packPricing.amount}
            currency="usd"
            packName={language === "es" ? packPricing.nameEs : packPricing.name}
            packThumbnail={packPricing.thumbnail}
            {...(checkoutAmounts?.showDiscountBadge && checkoutAmounts.discountPercentDisplay != null
              ? {
                  originalAmount: checkoutAmounts.originalListAmountCents,
                  discountPercent: checkoutAmounts.discountPercentDisplay,
                }
              : {})}
            language={language}
            onSuccess={handleSuccess}
            onError={handleError}
            onBack={handleBack}
            flowSurface="checkout_channel_invite_page"
          />
        </div>
      </Elements>
    </div>
  );
}
